import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Meta'nın CDN'i (scontent-*.fbcdn.net) tarayıcıdan doğrudan <img src> ile hotlink
// edilirse 403 döndürüyor (Sec-Fetch-Site tabanlı koruma) — bu yüzden görsel, kimlik
// doğrulamalı bir istekle sunucu proxy'sinden blob olarak çekiliyor.
const CreativeThumbnail = ({ adId, alt }) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    const load = async () => {
      try {
        const res = await axios.get(`/api/meta/ads/${adId}/thumbnail`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` },
          responseType: 'blob'
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      } catch (err) {
        if (!cancelled) setFailed(true);
      }
    };
    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [adId]);

  if (failed || !src) {
    return <span className="creative-thumb-placeholder">🖼️</span>;
  }
  return <img src={src} alt={alt} />;
};

const AdsTab = ({ showToast }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAds = async () => {
      try {
        const res = await axios.get('/api/meta/ads', {
          headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }
        });
        setAds(res.data || []);
      } catch (err) {
        console.error(err);
        showToast('Reklamlar yüklenirken hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchAds();
  }, [showToast]);

  if (loading) return <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Yükleniyor...</div>;

  if (ads.length === 0) {
    return (
      <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🖼️</div>
        <h3 style={{ fontSize: '18px', color: 'var(--text)', margin: '0 0 8px 0' }}>Henüz reklam verisi yok</h3>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>Bağlantı sekmesinden "Şimdi Senkronize Et" ile reklamlarınızı çekin.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text)' }}>Reklam Görselleri</h3>
        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Toplam {ads.length} reklam</span>
      </div>

      {/* Sınıf adları bilinçli olarak "ad"/"ads" içermiyor (creative-*) — reklam
          engelleyici tarayıcı eklentileri "ads-grid", "ad-card" gibi class adlarını
          jenerik kozmetik filtrelerle otomatik gizliyor (display:block !important
          inline stil enjekte ediyor). Bu yüzden liste görünmüyordu. */}
      <div className="creative-grid">
        {ads.map(ad => (
          <div key={ad.id} className="card creative-card">
            <div className="creative-thumb">
              {ad.preview_url ? (
                <CreativeThumbnail adId={ad.id} alt={ad.name} />
              ) : (
                <span className="creative-thumb-placeholder">🖼️</span>
              )}
            </div>
            <div className="creative-body">
              <div className="creative-name" title={ad.name}>{ad.name}</div>
              <div className="creative-context">
                {ad.campaign_name || 'Bilinmeyen kampanya'}{ad.adset_name ? ` · ${ad.adset_name}` : ''}
              </div>
              <span className={`badge ${ad.status === 'ACTIVE' ? 'badge-success' : 'badge-gray'}`} style={{ marginTop: '8px' }}>
                {ad.status}
              </span>
              <div className="creative-stats">
                <div>
                  <span className="creative-stat-label">Harcama</span>
                  <span className="creative-stat-value">₺{parseFloat(ad.spend || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                </div>
                <div>
                  <span className="creative-stat-label">Gösterim</span>
                  <span className="creative-stat-value">{parseInt(ad.impressions || 0).toLocaleString('tr-TR')}</span>
                </div>
                <div>
                  <span className="creative-stat-label">Tıklama</span>
                  <span className="creative-stat-value">{parseInt(ad.clicks || 0).toLocaleString('tr-TR')}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .creative-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          align-items: start;
          gap: 16px;
        }
        .creative-card { padding: 0; display: flex; flex-direction: column; }
        .creative-thumb {
          width: 100%;
          height: 180px;
          overflow: hidden;
          background: rgba(255,255,255,0.03);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .creative-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .creative-thumb-placeholder { font-size: 40px; opacity: 0.4; }
        .creative-body { padding: 14px; display: flex; flex-direction: column; }
        .creative-name {
          font-weight: 600; font-size: 13px; color: var(--text);
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .creative-context {
          font-size: 11px; color: var(--text-muted); margin-top: 2px;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .creative-stats {
          display: flex; justify-content: space-between; gap: 8px;
          margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--border);
        }
        .creative-stats > div { display: flex; flex-direction: column; }
        .creative-stat-label { font-size: 10px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; }
        .creative-stat-value { font-size: 13px; font-weight: 600; color: var(--text); margin-top: 2px; }
      `}</style>
    </div>
  );
};

export default AdsTab;
